/**
 * A list that changes which rows it shows moves the rows that stay, and fades in the rows that
 * come — C-023 batch 3 (`FND-1618`), the audit of 2026-10-27, class B/C.
 *
 * «все ▾» on the live roster put back every row the filter had taken out, and the rows that had
 * been on screen all along jumped down by 274 px in one frame on the audit's rig. This is FLIP:
 * where each row stood is read just before the change (`capture()`, called by the tap that makes
 * it), where it stands after is read before the browser paints, and each row that moved is run
 * from the old place to the new through `transform` — nothing is laid out per frame. A row that
 * was not there comes in through `opacity`. A row that leaves simply goes, as before.
 *
 * The rows are matched by the ids the list was drawn with, in order, so the row component needs
 * no attribute of its own. Nothing runs for somebody who asked for less movement, nor while the
 * app is off screen (a hidden document does not advance its timeline, `Unfold.tsx` has why), nor
 * when nothing was captured — a poll that redraws the list runs nothing.
 */
import { useLayoutEffect, useRef, type RefObject } from 'react';

const MS = 220;
// The arriving curve, as `Unfold` and `Push` use it.
const ARRIVING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

export function useFlip(
  list: RefObject<HTMLElement | null>,
  ids: readonly (number | string)[],
): () => void {
  /** The ids the list on screen was drawn with: what `capture()` reads its children against. */
  const drawn = useRef(ids);
  /** Where each row stood when the tap came, by id; null when nothing is pending. */
  const before = useRef<Map<number | string, number> | null>(null);

  const capture = () => {
    const element = list.current;
    if (element === null) return;
    const tops = new Map<number | string, number>();
    [...element.children].forEach((child, index) => {
      const id = drawn.current[index];
      if (id !== undefined) tops.set(id, child.getBoundingClientRect().top);
    });
    before.current = tops;
  };

  useLayoutEffect(() => {
    drawn.current = ids;
    const was = before.current;
    before.current = null;
    const element = list.current;
    if (was === null || element === null || was.size === 0) return;
    const still =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.visibilityState !== 'visible';
    if (still) return;
    [...element.children].forEach((child, index) => {
      const id = ids[index];
      if (id === undefined) return;
      const top = was.get(id);
      if (top === undefined) {
        child.animate([{ opacity: 0 }, { opacity: 1 }], { duration: MS, easing: ARRIVING });
        return;
      }
      const moved = top - child.getBoundingClientRect().top;
      if (Math.abs(moved) < 0.5) return;
      child.animate([{ transform: `translateY(${moved}px)` }, { transform: 'translateY(0)' }], {
        duration: MS,
        easing: ARRIVING,
      });
    });
  });

  return capture;
}
